import { BlockComponentFactory } from '../BlockFactory'
import { MarkdownEditorBlockProps } from './block-props.types'
import MdEditor from 'react-markdown-editor-lite'
import { convertMarkdownToHTML } from '../../../utils/markdown.utils'
import { useEffect, useRef } from 'react'
import { useMessage } from '../../../hooks/use-message'
import { handlePasteFunctionality } from '../../../common/common.utils'
import { LabelBlock } from './LabelBlock'
import { LABEL_FONT_STYLE } from '../../constants'
import { useBlockListeners } from '../BlockHooks'
import { useBlockEngine } from '../BlockEngineContext'
import { toCSSSize } from './block-utils'

export const MarkdownEditorBlockComponent =
    BlockComponentFactory.createBlockComponent<MarkdownEditorBlockProps>(props => {
        const editorRef = useRef<MdEditor>(null)
        const [messageApi] = useMessage()
        const {
            label,
            required,
            value,
            placeholder,
            width,
            height,
            visibility = 'visible',
            disabled = false,
            style,
            blockId,
            disablePasting = false,
        } = props

        const isEditable = useBlockEngine().blockEngine.getEditableSignal().value

        const [onChangeListener] = useBlockListeners<(value: string | null) => void>('onChange')

        const containerStyle: React.CSSProperties = {
            ...style,
            display: visibility === 'hidden' ? 'none' : 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: toCSSSize(width) || '100%',
        }

        useEffect(() => {
            const editor = editorRef.current
            if (editor) {
                const mdEditor = editor.getMdElement()
                if (mdEditor) {
                    const pasteHandler = (e: ClipboardEvent) => {
                        handlePasteFunctionality(e, !disablePasting, messageApi.error)
                    }
                    mdEditor.addEventListener('paste', pasteHandler)
                    return () => {
                        mdEditor.removeEventListener('paste', pasteHandler)
                    }
                }
            }
        }, [disablePasting, messageApi.error])

        return (
            <div style={containerStyle}>
                <LabelBlock label={label} required={required} labelStyle={LABEL_FONT_STYLE} />
                <MdEditor
                    ref={editorRef}
                    placeholder={placeholder}
                    id={blockId}
                    readOnly={!isEditable || disabled}
                    view={{ menu: isEditable && !disabled, md: true, html: true }}
                    value={value ?? ''}
                    style={{ height: toCSSSize(height) }}
                    renderHTML={text => {
                        try {
                            return convertMarkdownToHTML(text)
                        } catch (error) {
                            console.log('Markdown processing error: ', error)
                            return '<p>Error rendering content</p>'
                        }
                    }}
                    onChange={({ text }) => onChangeListener?.value?.(text)}
                />
            </div>
        )
    })
